import { Container, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from 'components/core/Grid';
import { Page } from 'components/layout/Page/Page';
import { CheckoutStep1 } from 'components/views/pages/Checkout/CheckoutStep1/CheckoutStep1';
// import { CheckoutStep2 } from 'components/views/pages/Checkout/CheckoutStep2/CheckoutStep2';
import { CheckoutStep3 } from 'components/views/pages/Checkout/CheckoutStep3/CheckoutStep3';
import { CheckoutStep4 } from 'components/views/pages/Checkout/CheckoutStep4/CheckoutStep4';
import { CheckoutSummary } from 'components/views/pages/Checkout/CheckoutSummary/CheckoutSummary';
import { useInitializeStep } from 'hooks/useInitializeStep';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { BookingGateWayModeIdEnum, calculateDifferenceInDays, FullBooking, getStripeToken, useBookingQuery, User } from '@alluradirect/react';
import { loadStripe } from '@stripe/stripe-js';
import { IS_LIVE_SITE } from 'config/envSetup';
import { Elements } from '@stripe/react-stripe-js';

import { BookingCheckoutResponse, useCheckout } from 'components/views/pages/Checkout/hooks/useCheckout';
import { httpsUrl } from 'config/url';
import { CheckoutParamEnum } from 'components/views/pages/Checkout/types';
import { useCheckoutFixInvalidParams } from 'components/views/pages/Checkout/hooks/useCheckoutFixInvalidParams';
import { useScreenWidth } from 'webHooks/useScreenWidth';
import { CheckoutSummaryProperty } from 'components/views/pages/Checkout/CheckoutSummary/components/CheckoutSummaryProperty';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';

import { useCheckoutParams } from 'components/views/pages/Checkout/hooks/useCheckoutParams';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import { StickyWrapper } from 'components/views/pages/Checkout/CheckoutSummary/styles';
import { LoginState, useLoginState } from 'components/views/pages/_general/useLoginState';
import { getNumberOrZero } from 'helpers/numHelpers';
import { CheckoutBoxWrapper } from 'components/views/pages/Checkout/styles';
import dayjs from 'dayjs';
import { usePropertyInformationQuery } from 'components/views/pages/Checkout/hooks/usePropertyInformationQuery';
import { dvLogo } from 'config/imageUrls';

// Initialize stripe
const stripeProvider = loadStripe(getStripeToken({ isLive: IS_LIVE_SITE() }));
const formatDate = (date: string) => dayjs(date).format('YYYY-MM-DD');

export interface CheckoutStepProps {
  startDate: string | undefined;
  endDate: string | undefined;
  pets: string | undefined;
  infants: string | undefined;
  adults: string | undefined;
  pChildren: string | undefined;
  paymentType: string | undefined;
  pid: string | undefined;
  bid: string | undefined;
  message: string | undefined;
  marketing: string | undefined;
  handleNextStep: (customParam?: { paramName: string; paramValue: any }) => void;
  checkoutData: BookingCheckoutResponse | undefined;
  userData: User | undefined;
  userId: number | undefined;
  bookingData: FullBooking | undefined;
  isInvalidDates: boolean;
  loginState: LoginState;
  isLastStep: boolean;
  isMakingPayment: boolean;
  setIsMakingPayment: React.Dispatch<React.SetStateAction<boolean>>;
  currency_code: string;
  currency_multiplier: number;
}

const StyledContainer = styled(Container)`
  ${props => props.theme.breakpoints.down('sm')} {
    padding: 0px !important;
  }
`;

const StyledGrid = styled(Grid)`
  ${props => props.theme.breakpoints.up('md')} {
    padding-right: ${props => props.theme.spacing(10)} !important;
  }
`;

const TitleGrid = styled(Grid)`
  margin-top: ${props => props.theme.spacing(3)} !important;
  margin-bottom: ${props => props.theme.spacing(3)} !important;

  ${props => props.theme.breakpoints.down('sm')} {
    margin-bottom: ${props => props.theme.spacing(2)} !important;
  }
`;

const steps: { title: string; component: (props: CheckoutStepProps) => any }[] = [
  { title: 'Get Started', component: CheckoutStep1 },
  //  { title: 'Property Rules', component: CheckoutStep2 },
  { title: 'Payment', component: CheckoutStep3 },
  { title: 'Payment Successful', component: CheckoutStep4 },
];

/** Called reservenow and in [pid]/reservenow to preserve backwards-compatibility
 */
const ReserveNow = () => {
  const { isXs } = useScreenWidth();
  const { isMd } = useScreenWidth();
  const theme = useTheme<any>();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));
  const router = useRouter();
  // In order for dates to be valid, the following conditions need to be met
  // 1. startDate and endDate need to be in the query param
  // 2. startDate and endDate need to be in the following format YYYY-MM-DD
  // 3. startDate needs to be before endDate (at least 1 day)
  // 4. Number of nights needs to satisfy the min nights for the range between startDate-endDate
  // 5. Date Range cannot conflict with any other bookings
  // 6. Date Range needs to be within a valid season (needs rates)

  // Synch with useStripePayment to lock parts of screen
  const [isMakingPayment, setIsMakingPayment] = useState(false);

  const {
    step,
    adults,
    pets,
    children,
    infants,
    startDate,
    endDate,
    paymentType,
    marketing,
    bid,
    message,
    propertyId,
    owner_gift_certificate_code,
    owner_gift_certificate_id,
    owner_offer_code,
    owner_offer_id,
    allura_gift_certificate_code,
    allura_gift_certificate_id,
    allura_offer_code,
    allura_offer_id,
    currency_code,
    currency_multiplier,
  } = useCheckoutParams();

  const maxStep = steps.length;
  const isLastStep = Number(step) === steps.length;
  const isFirstStep = Number(step) === 1;

  const currency_code_default = currency_code ? currency_code : 'CAD';
  const currency_multiplier_default = currency_multiplier ? currency_multiplier : 1;

  useInitializeStep({ maxStep });
  useCheckoutFixInvalidParams({ isFirstStep });

  const { userQuery, userId, loginState } = useLoginState({ propertyId });

  // for getting the checkout data without having to have guests
  const num_guests = (getNumberOrZero(adults) + getNumberOrZero(children)) || 1;
  const checkoutQueryArgs = {
    guestId: Number(userId),
    isDirectVacations: false,
    propertyId: Number(propertyId),
    arrival: startDate as string,
    departure: endDate as string,
    num_guests,
    num_pets: Number(pets),
    owner_gift_certificate_code,
    owner_gift_certificate_id,
    owner_offer_code,
    owner_offer_id,
    allura_gift_certificate_code,
    allura_gift_certificate_id,
    allura_offer_code,
    allura_offer_id,
    currency_code,
    currency_multiplier
  };

  const isCheckoutQueryEnabled =
    Boolean(propertyId) && Boolean(startDate) && Boolean(endDate) && !isLastStep && !isMakingPayment// && Boolean(adults);

  const { query: checkoutQuery, isInvalidDates } = useCheckout(checkoutQueryArgs, {
    enabled: isCheckoutQueryEnabled,
    hasToastsEnabled: true,
  });

  if (checkoutQuery.status === "error") {
    // very brittle, be careful not to change server values
    const errorMsg = checkoutQuery.error?.message || "";
    if (errorMsg.match(/Owner Offer/i)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            owner_offer_code: undefined,
            owner_offer_id: undefined,
          },
        },
        undefined,
        {
          shallow: false,
          scroll: false,
        },
      );
    }
    if (errorMsg.match(/Owner Gift/i)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            owner_gift_certificate_code: undefined,
            owner_gift_certificate_id: undefined,
          },
        },
        undefined,
        {
          shallow: false,
          scroll: false,
        },
      );
    }
    if (errorMsg.match(/Allura Offer/i)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            allura_offer_code: undefined,
            allura_offer_id: undefined,
          },
        },
        undefined,
        {
          shallow: false,
          scroll: false,
        },
      );
    }
    if (errorMsg.match(/Allura Gift/i)) {
      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            allura_gift_certificate_code: undefined,
            allura_gift_certificate_id: undefined,
          },
        },
        undefined,
        {
          shallow: false,
          scroll: false,
        },
      );
    }
  }

  const bookingQuery = useBookingQuery({
    isOwnerRole: false,
    isGuestRole: true,
    bookingId: Number(bid),
    isEnabled: Boolean(bid) && Boolean(userId) && isLastStep,
    guestId: Number(userId),
    includeTimeline: true,
    ownerOffer: owner_offer_code && owner_offer_id ? { code: encodeURIComponent(owner_offer_code), id: owner_offer_id } : undefined,
    alluraOffer: allura_offer_code && allura_offer_id ? { code: encodeURIComponent(allura_offer_code), id: allura_offer_id } : undefined,
    ownerGiftCertificate: owner_gift_certificate_code && owner_gift_certificate_id ? { code: encodeURIComponent(owner_gift_certificate_code), id: owner_gift_certificate_id } : undefined,
    alluraGiftCertificate: allura_gift_certificate_code && allura_gift_certificate_id ? { code: encodeURIComponent(allura_gift_certificate_code), id: allura_gift_certificate_id } : undefined,
  });

  const handleNextStep = (customParam?: { paramName: string; paramValue: any }) => {
    const newQuery: any = {
      ...router.query,
      [CheckoutParamEnum.step]: Number(step) + 1,
    };

    if (customParam) {
      newQuery[customParam.paramName] = customParam.paramValue;
    }

    router.push(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true, scroll: true },
    );
  };
  const handlePreviousStep = () => {
    // Route back to listing page if on the first step
    if (Number(step) === 1) {
      // Do not use next router as it will send to catch-all route
      if (startDate && endDate) {
        const localStorageKey = "lastSearchCriteria"
        let lastSearchCriteria = localStorage.getItem(localStorageKey)
        if (lastSearchCriteria) {
          try {
            const body = JSON.parse(lastSearchCriteria)
            body.ARRIVAL = formatDate(startDate)
            body.BESTARRIVAL = formatDate(startDate)
            body.DEPARTURE = formatDate(endDate)
            //TODO: add number_of_adults, number_of_kids?
            body.MINNIGHTS = calculateDifferenceInDays(new Date(startDate), new Date(endDate));
            lastSearchCriteria = JSON.stringify(body)
          } catch (e) {
            console.error(e)
          }
        } else {
          lastSearchCriteria = JSON.stringify({
            ARRIVAL: formatDate(startDate),
            BESTARRIVAL: formatDate(startDate),
            DEPARTURE: formatDate(endDate),
            MINNIGHTS: calculateDifferenceInDays(new Date(startDate), new Date(endDate)),
          })
        }
        localStorage.setItem(localStorageKey, lastSearchCriteria)
      }

      if (window) {
        window.location.href = `${httpsUrl()}/${propertyId}/`;
      }
      return;
    }
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, [CheckoutParamEnum.step]: Number(step) - 1 },
      },
      undefined,
      { shallow: true, scroll: true },
    );
  };

  const getCurrentStep = () => {
    if (!step) {
      return steps[0];
    }

    if (Number(step) > maxStep) {
      return steps[0];
    }

    if (Number(step) < 1) {
      return steps[0];
    }

    return steps[Number(step) - 1];
  };

  const currentStep = getCurrentStep();

  const stepTitle = useMemo(() => {
    if (isLastStep && bookingQuery.data) {
      const isGatewayClosed = bookingQuery.data.PROPERTY.GATEWAY_MODE.ID === BookingGateWayModeIdEnum.closed;
      if (isGatewayClosed) {
        return 'Payment Pending';
      }
      return currentStep.title;
    }

    return currentStep.title;
  }, [bookingQuery.data, currentStep.title, isLastStep]);

  const propertyInfo = usePropertyInformationQuery({ pid: Number(propertyId), enabled: Boolean(propertyId) });

  const dvLogoFilepath = propertyInfo.data
    ? `${propertyInfo.data.DATA.DV_LOGO_FILEPATH}`
    : dvLogo;

  return (
    <Elements stripe={stripeProvider}>
      <Page
        appSEOProps={{
          title: 'Checkout',
          description: 'Book your property today!',
          robots: 'noindex,follow',
        }}
        headerProps={{
          logoOnly: !mdUp,
          borderBottom: true,
          disabled: isMakingPayment,
          dvLogoFilepath: dvLogoFilepath
        }}
        isGreyBackground
      >
        <StyledContainer maxWidth="lg">
          <Grid container>
            <TitleGrid item xs={12}>
              <LoadingSkeleton isLoading={isLastStep && !bookingQuery.data} scaleOneToOne>
                <Typography variant="h4" pl={isXs ? "16px" : undefined}>
                  {isLastStep ? null : (
                    <IconButton
                      onClick={handlePreviousStep}
                      size="small"
                      sx={{ marginRight: 1, marginLeft: '-4px', marginTop: '-4px' }}
                      disabled={isMakingPayment}
                    >
                      <ArrowBackIosIcon />
                    </IconButton>
                  )}
                  {stepTitle}
                </Typography>
              </LoadingSkeleton>
            </TitleGrid>

            {isMd ? (
              <>
                <CheckoutBoxWrapper>
                  <Grid item xs={12}>
                    <CheckoutSummaryProperty pid={propertyId} />
                  </Grid>
                </CheckoutBoxWrapper>
                <Grid item xs={12}><AppDivider marginVertical="16px" borderWidth='0' /></Grid>
              </>

            ) : null}

            <StyledGrid item xs={12} md={7} mb={10}>
              <currentStep.component
                startDate={startDate}
                endDate={endDate}
                infants={infants}
                pChildren={children}
                adults={adults}
                pid={propertyId}
                pets={pets}
                paymentType={paymentType}
                userId={userId}
                bid={bid}
                marketing={marketing}
                handleNextStep={handleNextStep}
                checkoutData={checkoutQuery.data?.DATA}
                userData={userQuery.data?.DATA.USER ? userQuery.data.DATA.USER : undefined}
                bookingData={bookingQuery.data}
                isInvalidDates={isInvalidDates}
                loginState={loginState}
                message={message}
                isLastStep={isLastStep}
                isMakingPayment={isMakingPayment}
                setIsMakingPayment={setIsMakingPayment}
                currency_code={currency_code_default}
                currency_multiplier={currency_multiplier_default}
              />
            </StyledGrid>
            {!isMd ? (
              <Grid item sm={12} md={5}>
                <StickyWrapper>
                  <CheckoutSummary
                    pid={propertyId}
                    checkoutData={checkoutQuery.data?.DATA}
                    bookingData={bookingQuery.data}
                    paymentType={paymentType}
                    isLastStep={isLastStep}
                    currency_code={currency_code_default}
                    currency_multiplier={currency_multiplier_default}
                  />
                </StickyWrapper>
              </Grid>
            ) : null}
          </Grid>
        </StyledContainer>
      </Page>
    </Elements>
  );
};

export default ReserveNow;
